import type { GridCell, GridCoord } from '../../types/core';
import { cellKey, coordDistance, isInside, neighbours, sameCoord } from '../../utils/grid';

export function nearestRoad(coord: GridCoord, cells: readonly GridCell[], size: number): GridCoord | undefined {
  const cellByKey = new Map(cells.map((cell) => [cell.key, cell]));
  const visited = new Set<string>([cellKey(coord)]);
  const queue: GridCoord[] = [coord];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }

    const cell = cellByKey.get(cellKey(current));
    if (cell?.road) {
      return current;
    }

    for (const adjacent of neighbours(current)) {
      const key = cellKey(adjacent);
      if (isInside(adjacent, size) && !visited.has(key)) {
        visited.add(key);
        queue.push(adjacent);
      }
    }
  }

  return undefined;
}

export function findRoadPath(
  start: GridCoord,
  end: GridCoord,
  cells: readonly GridCell[],
  size: number
): GridCoord[] {
  if (sameCoord(start, end)) {
    return [start];
  }

  const cellByKey = new Map(cells.map((cell) => [cell.key, cell]));
  const startRoad = nearestRoad(start, cells, size) ?? start;
  const endRoad = nearestRoad(end, cells, size) ?? end;
  const startKey = cellKey(startRoad);
  const endKey = cellKey(endRoad);
  const queue: GridCoord[] = [startRoad];
  const visited = new Set<string>([startKey]);
  const cameFrom = new Map<string, GridCoord>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      break;
    }

    if (cellKey(current) === endKey) {
      break;
    }

    const candidates = neighbours(current).sort((a, b) => coordDistance(a, endRoad) - coordDistance(b, endRoad));
    for (const adjacent of candidates) {
      const key = cellKey(adjacent);
      const cell = cellByKey.get(key);
      const isEndpoint = key === endKey || key === startKey;

      if (isInside(adjacent, size) && !visited.has(key) && (cell?.road || isEndpoint)) {
        visited.add(key);
        cameFrom.set(key, current);
        queue.push(adjacent);
      }
    }
  }

  if (!cameFrom.has(endKey)) {
    return simpleManhattanPath(start, end);
  }

  const path: GridCoord[] = [endRoad];
  let cursor = endRoad;

  while (cellKey(cursor) !== startKey) {
    const previous = cameFrom.get(cellKey(cursor));
    if (!previous) {
      return simpleManhattanPath(start, end);
    }
    cursor = previous;
    path.push(cursor);
  }

  const roadPath = path.reverse();
  return [start, ...roadPath, end];
}

export function simpleManhattanPath(start: GridCoord, end: GridCoord): GridCoord[] {
  const path: GridCoord[] = [start];
  let cursor = { ...start };
  const stepX = Math.sign(end.x - start.x);

  while (cursor.x !== end.x) {
    cursor = { x: cursor.x + stepX, z: cursor.z };
    path.push(cursor);
  }

  const stepZ = Math.sign(end.z - cursor.z);

  while (cursor.z !== end.z) {
    cursor = { x: cursor.x, z: cursor.z + stepZ };
    path.push(cursor);
  }

  return path;
}
